import {
    ALL_NAV_REGISTRY, NAV_RECRUITING, NAV_PERSONAL, NAV_CONTRACTOR,
    NAV_HR, NAV_OPERATIONS, NAV_FINANCE, NAV_APPROVALS, NAV_DEPT_HEAD,
    NAV_CEO, NAV_ONLINE_MANAGER, NAV_PARTNERS, NAV_IT_ADMIN,
} from './navConfig'
import { Home } from 'lucide-react'
import { SETTINGS_GROUPS } from './SettingsMenu'
import { NAV_GROUPS, groupKeyForPathname, resolveGroupTarget, routeKeyFromPathname } from './navGroups'

const TEAM_ROLES = ['dept_head', 'cmo', 'head']

/** Presentation only: visibility is always supplied by the existing ModuleContext. */
export function getWorkspaceShortcuts(user, isVisible) {
    const route = to => ALL_NAV_REGISTRY.find(item => item.to === to && isVisible(item.moduleKey || item.to || 'dashboard'))
    const group = key => resolveGroupTarget(key, isVisible)
    const home = user?.role === 'online_manager' ? route('manager-dashboard')
        : user?.role === 'marketing_contractor' ? route('contractor-cabinet') : route('')
    const team = group('dept-team') || group('people')
    const candidates = group('candidates')
    const operations = route('finance-operations') || group('equipment')
    const analytics = route('executive') || route('finance-analytics') || group('hiring-analytics')
    const choices = [
        home && { ...home, label: 'Обзор', activeRoutes: ['start', 'dashboard'] },
        route('inbox') && { ...route('inbox'), label: 'Входящие' },
        team && { ...team, label: 'Команда' },
        candidates && { ...candidates, label: 'Подбор' },
        operations,
        analytics,
    ].filter(Boolean)
    return choices.filter((item, index) => choices.findIndex(other => other.to === item.to) === index)
}

/** The old section inventory is preserved, including role-specific ordering/exclusions. */
export function getWorkspaceSections(user, personalNav = NAV_PERSONAL) {
    const department = { title: TEAM_ROLES.includes(user?.role) ? 'Мой отдел' : 'Руководитель подразделения', items: NAV_DEPT_HEAD }
    const strategy = { title: 'Стратегия компании', items: NAV_CEO }
    return [
        TEAM_ROLES.includes(user?.role) && department,
        user?.role === 'owner' && strategy,
        { title: 'HR Рекрутинг', items: NAV_RECRUITING.filter(item => !item.end || user?.role !== 'online_manager')
            .flatMap(item => item.groupKey === 'offers' ? [item, ...NAV_APPROVALS] : [item]) },
        { title: 'Личный кабинет', items: user?.role === 'marketing_contractor' ? NAV_CONTRACTOR : personalNav },
        user?.role !== 'online_manager' && { title: 'HR Управление', items: NAV_HR },
        { title: 'Операции', items: NAV_OPERATIONS },
        { title: 'Финансы', items: NAV_FINANCE },
        !TEAM_ROLES.includes(user?.role) && department,
        { title: 'Онлайн-менеджер', items: NAV_ONLINE_MANAGER },
        user?.role !== 'owner' && strategy,
        { title: 'Партнёрка', items: NAV_PARTNERS },
        { title: 'IT / Администрирование', items: NAV_IT_ADMIN },
    ].filter(Boolean)
}

const SECTION_NAMES = {
    'HR Рекрутинг': 'Подбор персонала', 'Личный кабинет': 'Моя работа',
    'HR Управление': 'Сотрудники и отделы', 'Руководитель подразделения': 'Управление командой',
    'Партнёрка': 'Партнёры и CRM', 'IT / Администрирование': 'Поддержка и задачи',
    'Онлайн-менеджер': 'Работа онлайн-команды',
}

const WORKSPACE_SECTION_BY_ROUTE = new Map(getWorkspaceSections({ role: 'admin' }).flatMap(section => section.items.flatMap(item => item.groupKey
    ? (NAV_GROUPS[item.groupKey]?.tabs || []).map(tab => [tab.to, SECTION_NAMES[section.title] || section.title])
    : item.to !== undefined ? [[item.to, SECTION_NAMES[section.title] || section.title]] : [])))
const SETTINGS_ROUTES = new Set(SETTINGS_GROUPS.flatMap(section => section.items.map(item => item.to)))

export function isSidebarWorkRoute(item) {
    return !SETTINGS_ROUTES.has(item.to) || WORKSPACE_SECTION_BY_ROUTE.has(item.to)
}

const ROUTE_NAMES = {
    'growth-me': 'Мой план развития', 'dialogue-trainer': 'Тренажёр разговоров', 'me/skill-profile': 'Мои навыки',
    'internal-jobs': 'Вакансии внутри компании', 'ai-studio': 'Студия ИИ',
    'teambuilder': 'Подбор состава команды', 'workflows': 'Конструктор процессов',
    'pipeline': 'Воронка подбора', 'process': 'Этапы процесса подбора',
    'hh-responses': 'Отклики на HH.ru', 'hh-messenger': 'Переписка на HH.ru', 'hh-vacancy-stats': 'Статистика вакансий на HH.ru',
    'partner-plan-gap': 'Мой план работы с партнёрами', 'partner-team-compare': 'Мои результаты и результаты команды',
    'partner-team': 'Команда по работе с партнёрами', 'partner-network-tree': 'Структура партнёрской сети',
    'partner-crm/geo-leads': 'Потенциальные партнёры на карте', 'partner-analytics-suite': 'Аналитика партнёров',
    'partner-exec': 'Стратегия работы с партнёрами', 'partner-products': 'Продукты для партнёров',
    'partner-docs-marketing': 'Материалы для партнёров', 'partner-docs-contracts': 'Договоры с партнёрами',
    '': 'Главная', 'employees': 'Сотрудники', 'orgchart': 'Структура компании',
    'myteam': 'Сотрудники отдела', 'my-team': 'Сотрудники отдела',
    'inbox': 'Мои задачи', 'resume-scraper': 'Поиск резюме',
    'shortlist': 'Отобранные кандидаты', 'offers': 'Предложения о работе', 'hh-search': 'Поиск резюме на HH.ru',
    'offboardings': 'Увольнения', 'hr-lifecycle': 'Приём и адаптация',
    'onboarding-paths': 'Программы адаптации', 'onboarding-dashboard': 'Результаты адаптации',
    'reviews': 'Оценка сотрудников', 'people-analytics': 'Аналитика сотрудников',
    'team/learning': 'Прогресс обучения команды', 'marketing': 'Маркетинг: показатели',
    'exchange': 'Показатели обменных операций', 'dept-dashboard': 'Показатели отдела', 'team-payments': 'Выплаты сотрудникам команды', 'partner-desk': 'Мои партнёры и задачи',
    'manager-dashboard': 'Показатели моей работы', 'support-board': 'Обращения в поддержку',
    'wiki': 'База знаний и инструкции', 'partner-crm': 'База партнёров',
}

export function getNavigationDisplayItem(item) {
    return { ...item, label: ROUTE_NAMES[item.to] || item.label, section: WORKSPACE_SECTION_BY_ROUTE.get(item.to) || SECTION_NAMES[item.section] || item.section,
        activeRoutes: item.to === '' ? ['start', 'dashboard'] : item.activeRoutes, searchLabels: [item.label, item.section, ...(item.searchLabels || [])] }
}

/** Expand accessible tabs before counting children; never expose a hidden tab. */
export function getAccordionSections(user, personalNav, isVisible) {
    const seen = new Set()
    return getWorkspaceSections(user, personalNav).map(section => {
        const title = SECTION_NAMES[section.title] || section.title
        const items = section.items.flatMap(item => item.subLabel ? [] : item.groupKey
            ? (NAV_GROUPS[item.groupKey]?.tabs || []).filter(tab => isVisible(tab.moduleKey || tab.to || 'dashboard')).map(tab => ({ ...tab, groupLabel: NAV_GROUPS[item.groupKey].label }))
            : isVisible(item.moduleKey || item.to || 'dashboard') ? [item] : [])
            .filter(item => { if (seen.has(item.to)) return false; seen.add(item.to); return true })
            .map(item => ({ ...getNavigationDisplayItem(item), section: title }))
        return { title, items }
    }).filter(section => section.items.length > 0)
}

// Рубильник Главной: пока выключен, меню и вход ведут себя как раньше.
export const HOME_FLAG = 'home_v1'

export const HOME_NAV_ITEM ={ to: 'home', icon: Home, label: 'Главная', activeRoutes: ['start'], section: 'Главная' }

/**
 * Главная (рубильник home_v1) — первый пункт меню, над всеми разделами.
 * Индекс `''` с ней перестаёт быть «Главной»: вход уводит его на /home, поэтому
 * пункт превращается в явный «Дашборд» (/dashboard) — иначе в меню было бы
 * два пункта с одним смыслом.
 */
export function withHomeEntry(sections, homeEnabled) {
    if (!homeEnabled) return sections
    const remapped = sections.map(section => ({
        ...section,
        items: section.items.map(item => (item.to === ''
            ? { ...item, to: 'dashboard', label: 'Дашборд', end: false, activeRoutes: undefined }
            : item)),
    }))
    return [{ title: 'Главная', items: [HOME_NAV_ITEM] }, ...remapped]
}

export function isWorkspaceLinkActive(item, pathname) {
    if (item.groupKey && groupKeyForPathname(pathname) === item.groupKey) return true
    const route = routeKeyFromPathname(pathname)
    if (item.activeRoutes?.includes(route)) return true
    return item.end ? route === item.to : route === item.to || route.startsWith(`${item.to}/`)
}

/** Choose the most specific accessible route, so a subpage has one selection. */
export function getActiveWorkspaceRoute(items, pathname) {
    const path = String(pathname || '').replace(/^\/erp(?=\/|$)/, '').replace(/^\//, '').replace(/\/$/, '')
    const matches = items.filter(item => item.to === path || item.activeRoutes?.includes(path) || (item.to && path.startsWith(`${item.to}/`)))
    return matches.sort((a, b) => b.to.length - a.to.length)[0]?.to ?? null
}
